/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { Checkbox, Grid, makeStyles, Typography } from '@material-ui/core';
import ApplicationState from 'store/interfaces';
import { connect } from 'react-redux';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import {
  BulkClientsFields,
  EnvironmentFields,
  ListBulkClientsProps,
} from 'modules/environments/interfaces';
import i18n from 'i18n';
import Button from 'components/Button';
import { modalClose, modalOpen } from 'modules/modal/actions';
import {
  editBulkClientsRequest,
  getBulkClientsRequest,
} from 'modules/environments/actions';
import { useTableFilters } from 'hooks/useTableFilters';
import TitleDivider from 'components/TitleDivider';
import { Form, Formik } from 'formik';
import DynamicTextField from 'components/DynamicTextField';
import { ModalFields } from 'modules/modal/interfaces';
import { SearchBar } from 'components/SearchBar';
import VerificationCode from 'components/VerificationCode';
import styles from '../styles';
import { initialQuery } from '..';
import schema from '../schema';
import screenFields from '../screenFields';

const useStyles = makeStyles(styles);

export interface ConnectionTypeConfigProps {
  bulkClients: ListBulkClientsProps;
  isLoadingBulkClients: boolean;
  environment: EnvironmentFields | null;
  verificationCodeRef: React.MutableRefObject<string[]>;
  setVerificationCode: React.Dispatch<React.SetStateAction<string[]>>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  getBulkClientsRequest: (props) => void;
  editBulkClientsRequest: (props) => void;
}

const ConnectionTypeConfig = ({
  bulkClients,
  isLoadingBulkClients,
  environment,
  verificationCodeRef,
  setVerificationCode,
  modalOpen,
  modalClose,
  getBulkClientsRequest,
  editBulkClientsRequest,
}: ConnectionTypeConfigProps) => {
  const classes = useStyles();

  interface FormattedObject {
    cli_uuids: string[];
    connection_type_configuration: {
      connection_type_enabled: boolean;
      connection_type: 'public' | 'private' | null;
    };
  }
  const [checks, setChecks] = React.useState<BulkClientsFields[]>(
    bulkClients.results.filter(
      item => item.connection_type_configuration.connection_type_enabled
    )
  );

  const {
    query,
    searchValue,
    itemOrder,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const getBulkClients = () => {
    getBulkClientsRequest({
      id: environment?.environment_uuid,
      query: { ...query },
    });
  };

  const connectionTypeEnum = {
    private: i18n.t('BULK_CLIENTS.PRIVATE_KEY'),
    public: i18n.t('BULK_CLIENTS.PUBLIC_KEY'),
  };

  const enabledEnum = {
    false: i18n.t('NO_KEY'),
    true: i18n.t('YES_KEY'),
  };

  React.useEffect(() => {
    getBulkClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBulkClientsRequest, query]);

  React.useEffect(() => {
    setChecks(
      bulkClients.results.filter(
        item => item.connection_type_configuration.connection_type_enabled
      )
    );
  }, [bulkClients]);

  const handleCheck = (uuid: string) => {
    setChecks(prevChecks => {
      const existingItem = prevChecks.find(item => item.client_uuid === uuid);

      if (existingItem) {
        return prevChecks.map(item =>
          item.client_uuid === uuid
            ? {
                ...item,
                connection_type_configuration: {
                  ...item.connection_type_configuration,
                  connection_type_enabled: !item.connection_type_configuration
                    .connection_type_enabled,
                },
              }
            : item
        );
      }
      const newItem = bulkClients.results.find(
        item => item.client_uuid === uuid
      );

      if (newItem) {
        const updatedNewItem = {
          ...newItem,
          connection_type_configuration: {
            ...newItem.connection_type_configuration,
            connection_type_enabled: true,
          },
        };
        return [...prevChecks, updatedNewItem];
      }

      return prevChecks;
    });
  };

  const updateChecks = (
    client_uuid: string,
    newConnectionType: 'public' | 'private' | null
  ) => {
    setChecks(prevChecks =>
      prevChecks.map(check =>
        check.client_uuid === client_uuid
          ? {
              ...check,
              connection_type_configuration: {
                ...check.connection_type_configuration,
                connection_type: newConnectionType,
              },
            }
          : check
      )
    );
  };

  const headers = [
    {
      key: 'nome',
      i18n: 'CLIENT_NAME_KEY',
      order: true,
    },
    {
      key: 'connection_type_enabled',
      i18n: 'BULK_CLIENTS.ACTIVE_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'connection_type',
      i18n: 'BULK_CLIENTS.PRIVATE_KEY',
      centerTitle: true,
    },
    {
      key: 'connection_type',
      i18n: 'BULK_CLIENTS.PUBLIC_KEY',
      centerTitle: true,
    },
  ];

  const formatObjects = (client: BulkClientsFields[]): FormattedObject[] => {
    return client.map(obj => ({
      cli_uuids: [obj.client_uuid],
      connection_type_configuration: {
        connection_type_enabled:
          obj.connection_type_configuration.connection_type_enabled,
        connection_type: obj.connection_type_configuration.connection_type,
      },
    }));
  };

  const formattedArray = formatObjects(checks);
  const handleConfirm = (values: any) => {
    const verificationCodeToSend = verificationCodeRef.current;
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
          restricted_action_code: verificationCodeToSend.join(''),
        },
        clients_data: formattedArray,
      },
    });
    modalClose();
  };

  const handleSubmit = (values: any) => {
    setVerificationCode(Array(6).fill(''));
    editBulkClientsRequest({
      id: environment?.environment_uuid,
      dataValues: {
        operator_data: {
          action_reason: values?.action_reason,
        },
        clients_data: formattedArray,
      },
    });
    modalOpen({
      title: i18n.t('VERIFICATION_CODE_KEY'),
      content: (
        <VerificationCode
          length={6}
          setVerificationCode={setVerificationCode}
          onRetry={() => {
            editBulkClientsRequest({
              id: environment?.environment_uuid,
              dataValues: {
                operator_data: {
                  action_reason: values?.action_reason,
                },
                clients_data: formattedArray,
              },
            });
          }}
          onConfirm={() => {
            handleConfirm(values);
          }}
        />
      ),
      fullWidth: true,
    });
  };

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid container>
        <SearchBar
          id="search-client"
          isLoading={isLoadingBulkClients}
          searchValue={searchValue}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: BulkClientsFields, index) => (
            <TableRow key={index}>
              <TableCell>{item?.name}</TableCell>
              <TableCell textAlignCenter centerTitle>
                <Checkbox
                  className={classes.checkbox}
                  onChange={() => {
                    handleCheck(item.client_uuid);
                    updateChecks(item.client_uuid, 'private');
                  }}
                  checked={
                    !!checks.find(
                      check =>
                        check?.client_uuid === item?.client_uuid &&
                        check.connection_type_configuration
                          .connection_type_enabled === true
                    )
                  }
                />
              </TableCell>
              <TableCell textAlignCenter centerTitle>
                <Checkbox
                  className={classes.checkbox}
                  onChange={() => updateChecks(item.client_uuid, 'private')}
                  checked={
                    !!checks.find(
                      check =>
                        check?.client_uuid === item?.client_uuid &&
                        check?.connection_type_configuration.connection_type ===
                          'private' &&
                        check.connection_type_configuration
                          .connection_type_enabled === true
                    )
                  }
                  disabled={
                    !checks.find(
                      check =>
                        check?.client_uuid === item?.client_uuid &&
                        check.connection_type_configuration
                          .connection_type_enabled === true
                    )
                  }
                />
              </TableCell>
              <TableCell textAlignCenter centerTitle>
                <Checkbox
                  className={classes.checkbox}
                  onChange={() => updateChecks(item.client_uuid, 'public')}
                  checked={
                    !!checks.find(
                      check =>
                        check?.client_uuid === item?.client_uuid &&
                        check?.connection_type_configuration.connection_type ===
                          'public' &&
                        check.connection_type_configuration
                          .connection_type_enabled === true
                    )
                  }
                  disabled={
                    !checks.find(
                      check =>
                        check?.client_uuid === item?.client_uuid &&
                        check.connection_type_configuration
                          .connection_type_enabled === true
                    )
                  }
                />
              </TableCell>
            </TableRow>
          )}
          overrideRowsCount={10}
          isLoading={isLoadingBulkClients}
          data={bulkClients}
          onChangeLimit={handleLimitChange}
          onChangePage={handlePageChange}
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="activate-button"
          submitButton
          onClick={() => {
            modalOpen({
              title: i18n.t(
                'BULK_CLIENTS.CONFIRM_ACTIVATE_CONNECTION_TYPE_KEY'
              ),
              content: (
                <>
                  <>
                    <Grid container justify="center">
                      <Typography style={{ fontSize: 12, marginBottom: 12 }}>
                        {i18n.t(
                          'BULK_CLIENTS.CONFIRM_ACTIVATE_CONNECTION_TYPE_IN_CLIENTS_KEY'
                        )}
                      </Typography>
                    </Grid>
                    <TableItems
                      header={
                        <>
                          <TableCell className={classes.tableRow}>
                            {i18n.t('CLIENT_NAME_KEY')}
                          </TableCell>
                          <TableCell
                            textAlignCenter
                            centerTitle
                            className={classes.tableRow}
                          >
                            {i18n.t('BULK_CLIENTS.ACTIVE_KEY')}
                          </TableCell>
                          <TableCell
                            textAlignCenter
                            centerTitle
                            className={classes.tableRow}
                          >
                            {i18n.t('BULK_CLIENTS.CONNECTION_TYPE_KEY')}
                          </TableCell>
                        </>
                      }
                      renderItems={(item: BulkClientsFields, index) => (
                        <TableRow key={index}>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell textAlignCenter centerTitle>
                            {
                              enabledEnum[
                                String(
                                  item?.connection_type_configuration
                                    .connection_type_enabled
                                )
                              ]
                            }
                          </TableCell>
                          <TableCell textAlignCenter centerTitle>
                            {item?.connection_type_configuration
                              .connection_type &&
                            item?.connection_type_configuration
                              .connection_type_enabled
                              ? connectionTypeEnum[
                                  item?.connection_type_configuration
                                    .connection_type
                                ]
                              : '-'}
                          </TableCell>
                        </TableRow>
                      )}
                      isLoading={isLoadingBulkClients}
                      data={{
                        count: 0,
                        next: '',
                        previous: '',
                        results: checks,
                      }}
                      onChangeLimit={handleLimitChange}
                      onChangePage={handlePageChange}
                      notFoundText={i18n.t('NO_CLIENT_KEY')}
                      notFooter
                      smallLoading={true}
                    />
                  </>
                  <TitleDivider
                    overrideClasses={{
                      title: { margin: '12px 4px 8px 4px', fontSize: 12 },
                    }}
                  >
                    {i18n.t('BULK_CLIENTS.SET_CONNECTION_TYPE_TO_CONFIRM_KEY')}
                  </TitleDivider>
                  <Formik
                    initialValues={{ action_reason: '' }}
                    enableReinitialize
                    validationSchema={schema}
                    validateOnChange
                    validateOnBlur
                    onSubmit={handleSubmit}
                  >
                    {({ setFieldValue, errors, values, submitForm }) => {
                      return (
                        <React.Fragment>
                          <Form>
                            <Grid container>
                              <Grid
                                container
                                spacing={2}
                                className={classes.fields}
                              >
                                <DynamicTextField
                                  {...screenFields[0]}
                                  setFieldValue={setFieldValue}
                                  value={values?.action_reason}
                                  errors={errors}
                                />
                              </Grid>
                            </Grid>
                            <Grid
                              container
                              justify="flex-end"
                              style={{ marginTop: 12 }}
                            >
                              <Button
                                submitButton
                                id="confirm-button"
                                onClick={submitForm}
                                isLoading={isLoadingBulkClients}
                              >
                                {i18n.t('CONTINUE_KEY')}
                              </Button>
                              <Button
                                id="cancel-button"
                                variant="outlined"
                                onClick={modalClose}
                              >
                                {i18n.t('CANCEL_KEY')}
                              </Button>
                            </Grid>
                          </Form>
                        </React.Fragment>
                      );
                    }}
                  </Formik>
                </>
              ),
            });
          }}
        >
          {i18n.t('NEXT_KEY')}
        </Button>
        <Button id="cancel-button" variant="outlined" onClick={modalClose}>
          {i18n.t('CLOSE_KEY')}
        </Button>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { data },
  environments: { bulkClients, isLoadingBulkClients },
}: ApplicationState) => ({
  role: data?.role,
  bulkClients,
  isLoadingBulkClients,
});

export default connect(mapStateToProps, {
  modalOpen,
  modalClose,
  getBulkClientsRequest,
  editBulkClientsRequest,
})(ConnectionTypeConfig);
